import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { ListChecks, Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { req } from '../../services/api';

// Iter58 — req importado de services/api.js (antes había def local duplicada)

/**
 * Tarjeta de configuración de sincronización de tareas con Microsoft To Do
 * y Google Tasks por cuenta correo. Iter56.
 */
export default function TasksSyncCard() {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listas, setListas] = useState({}); // { cuenta_id: [{id,name}] }
  const [saving, setSaving] = useState({}); // { cuenta_id: bool }
  const [syncing, setSyncing] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const d = await req('/api/tasks-sync/cuentas');
      setCuentas(Array.isArray(d) ? d : []);
    } catch (e) { toast.error(`Tasks sync: ${e.message}`); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const cargarListasRemoto = async (cuenta) => {
    if (listas[cuenta.id]) return; // ya cargadas
    try {
      const d = await req(`/api/tasks-sync/cuentas/${cuenta.id}/listas`);
      setListas((prev) => ({ ...prev, [cuenta.id]: d.listas || [] }));
    } catch (e) {
      const m = String(e.message || '');
      if (m.includes('403') || /reconect|scope|insuficiente/i.test(m)) {
        toast.error('Reconecta la cuenta en la sección de correos para autorizar el scope de tareas.', { duration: 8000 });
      } else {
        toast.error(`No se pueden listar tareas: ${e.message}`);
      }
      setListas((prev) => ({ ...prev, [cuenta.id]: [] }));
    }
  };

  const guardarConfig = async (cuenta, sync_enabled, list_id, list_name) => {
    setSaving((p) => ({ ...p, [cuenta.id]: true }));
    try {
      await req(`/api/tasks-sync/cuentas/${cuenta.id}/config`, {
        method: 'PUT',
        body: JSON.stringify({ sync_enabled, list_id, list_name }),
      });
      toast.success(sync_enabled ? `Sync activado: ${list_name}` : 'Sync desactivado');
      cargar();
    } catch (e) { toast.error(e.message); }
    finally { setSaving((p) => ({ ...p, [cuenta.id]: false })); }
  };

  const sincronizarAhora = async () => {
    setSyncing(true);
    try {
      const r = await req('/api/tasks-sync/sync-now', { method: 'POST' });
      const c = r.pull?.creados_crm || 0;
      const u = r.push?.creados?.length + r.push?.actualizados?.length || 0;
      toast.success(`Sync completada · ${c} importadas · ${u} exportadas`);
      cargar();
    } catch (e) { toast.error(`Sync: ${e.message}`); }
    finally { setSyncing(false); }
  };

  if (loading) {
    return (
      <Card className="p-5" data-testid="tasks-sync-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-brand-600 mx-auto" />
      </Card>
    );
  }

  const cuentasSoportadas = cuentas.filter((c) => c.soportado);

  return (
    <Card className="p-5" data-testid="tasks-sync-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-brand-600" /> Sincronizar tareas con Microsoft To Do / Google Tasks
          </h2>
          <p className="text-xs text-ink-500 mt-1">
            Las tareas con vencimiento se mantienen sincronizadas en ambos sentidos. CRM siempre gana en conflictos.
          </p>
        </div>
        {cuentas.some((c) => c.sync_enabled) && (
          <Button size="sm" variant="outline" onClick={sincronizarAhora} disabled={syncing} data-testid="btn-sync-tareas-now">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Sincronizar ahora</>}
          </Button>
        )}
      </div>

      {cuentasSoportadas.length === 0 && (
        <div className="text-sm text-ink-500 py-3 px-3 bg-ink-50 border border-ink-200 rounded">
          Solo cuentas <strong>Microsoft 365</strong> y <strong>Google</strong> soportan sync de tareas. Conecta una en la sección anterior.
        </div>
      )}

      {cuentasSoportadas.length > 0 && (
        <ul className="space-y-3" data-testid="tasks-sync-list">
          {cuentasSoportadas.map((c) => (
            <li key={c.id} className="border border-ink-200 rounded p-3 bg-white" data-testid={`tasks-sync-cuenta-${c.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge className={`text-[9px] border ${c.tipo === 'microsoft' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                    {c.tipo === 'microsoft' ? 'Microsoft 365' : 'Google'}
                  </Badge>
                  <span className="text-sm font-medium text-ink-900 truncate">{c.email}</span>
                  {c.sync_enabled && (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] border" data-testid={`badge-sync-on-${c.id}`}>
                      <CheckCircle2 className="w-2.5 h-2.5 mr-0.5 inline" /> Activo
                    </Badge>
                  )}
                </div>
                <label className="flex items-center gap-2 text-xs text-ink-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={c.sync_enabled}
                    disabled={saving[c.id]}
                    onChange={async (e) => {
                      const enabled = e.target.checked;
                      if (enabled) {
                        // cargar listas y mostrar selector
                        await cargarListasRemoto(c);
                      } else {
                        await guardarConfig(c, false, null, null);
                      }
                    }}
                    data-testid={`toggle-sync-${c.id}`}
                  />
                  <span>Sincronizar</span>
                </label>
              </div>

              {/* Selector de lista cuando hay listas cargadas y aún no hay list_id */}
              {!c.sync_enabled && listas[c.id]?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs flex items-center gap-2 flex-wrap" data-testid={`select-lista-${c.id}`}>
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span className="text-amber-900">Selecciona la lista destino:</span>
                  <select
                    className="border border-amber-300 rounded px-2 py-1 bg-white text-xs"
                    onChange={(e) => {
                      const lista = listas[c.id].find((l) => l.id === e.target.value);
                      if (lista) guardarConfig(c, true, lista.id, lista.name);
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>— elige —</option>
                    {listas[c.id].map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {c.sync_enabled && (
                <div className="text-[11px] text-ink-500">
                  📋 Lista destino: <strong className="text-ink-700">{c.list_name || c.list_id}</strong>
                  {c.last_sync_at && (
                    <span className="ml-2">· última sync: {new Date(c.last_sync_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}</span>
                  )}
                </div>
              )}

              {saving[c.id] && <div className="text-[10px] text-ink-400 mt-1"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Guardando…</div>}
            </li>
          ))}
        </ul>
      )}

      <p className="text-[10px] text-ink-400 mt-3">
        ⚠️ Si tu cuenta Google se conectó antes de Iter56 te pediremos reconectarla para autorizar el scope <code>tasks</code>.
        Solo se sincronizan tareas con fecha de vencimiento.
      </p>
    </Card>
  );
}
